/**
 * feat-166 Slice (frontend) — the reading-item detail pane.
 *
 * A reading item is explanatory text (markdown SOURCE, stored + served as raw
 * markdown per ADR 0001 D5) plus an OPTIONAL ordered list of images the learner
 * reads before tapping Continue. It grants NO XP and has no async job — strictly
 * simpler than the feat-165 video item: no recorder, no captions, no status poll.
 *
 * Mirrors VideoDetailForm's public shape (the same right-detail-pane slot the
 * feat-107 3-pane editor mounts) but collapses all video/caption machinery down
 * to: a title input (optional), a markdown textarea + live preview, and an
 * ordered multi-image uploader (reuses feat-165's `lesson-images/*` presign via
 * {@link uploadReadingImage}). CREATE (existingId == null) → createReadingItem;
 * EDIT → updateReadingItem. Image content-type/size are pre-checked client-side
 * (S3 is the authority) against the shared Lesson-image allowlist.
 *
 * feat-259: THIS PANEL AUTO-SAVES. Save and Cancel are gone; Delete has moved
 * out of the header into the shared footer, and its ink is now `danger-text`
 * (brand coral is never a destructive colour — design/NOTES.md §3). The pane is
 * RESTRUCTURED, not just rearranged: this component now returns the `<aside>`
 * itself, with a scroll child plus `shrink-0` banner and footer siblings, the
 * way `QuestionDetailForm` has since feat-231. Dropping a footer into the old
 * `overflow-y-auto` aside would have scrolled it away with the fields — the
 * exact bug feat-231 fixed, passing a DOM-shape comparison while it did.
 *
 * Markdown-preservation caveat (ADR 0001 D5): the preview is a lightweight,
 * SAFE renderer (React nodes only — never dangerouslySetInnerHTML) covering
 * headings / bold / italic / bullet lists. It is authoring feedback, not the
 * mobile render (v1 mobile shows plain paragraphs).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LESSON_IMAGE_ALLOWED_TYPES,
  LESSON_IMAGE_MAX_BYTES,
  lessonImagePublicUrl,
  uploadReadingImage,
  createReadingItem,
  updateReadingItem,
} from '../../../services/quizContentAdminService';
import type { ReadingItemSummary } from '../../../services/quizContentAdminService';
import ContentTimestamps from './ContentTimestamps';
import { useAutoSave, PanelSaveWiring } from './useAutoSave';
import { ItemDetailFooter, SaveErrorBanner } from './ItemDetailFooter';
import {
  ApprovedFieldMarker,
  ApprovedValueLine,
  PanelApprovalState,
} from './approval/itemApproval';
import { QUIZ_TERMS } from '../quizTerminology';
import {
  EditPreviewToggle,
  MarkdownPreview,
  READING_BODY_PLACEHOLDER,
  READING_BODY_SOFT_WARN_CHARS,
  READING_PREVIEW_PARITY_NOTE,
  readingCharCountText,
  type ReadingEditorMode,
} from './readingMarkdown';

function humanBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(0)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}

// ── shared small UI (mirrors VideoDetailForm) ────────────────────────────────

const Spinner: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <span
    className="inline-block rounded-full border-2 border-reading border-t-transparent animate-spin"
    style={{ width: size, height: size }}
    role="status"
    aria-label="Loading"
  />
);

const ErrorBanner: React.FC<{ title: string; children?: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-2xl border border-danger-line/30 bg-danger-surface p-3 text-sm">
    <div className="flex items-start gap-2">
      <span className="text-danger text-lg leading-none">⚠</span>
      <div>
        <div className="font-semibold text-danger-text">{title}</div>
        {children && <div className="text-inkMuted mt-0.5">{children}</div>}
      </div>
    </div>
  </div>
);

// ── ordered image list ───────────────────────────────────────────────────────

/** A staged image: its persisted S3 key + a public URL for the thumbnail. */
interface StagedImage {
  s3_key: string;
  url: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Public entry — the right detail pane for a reading item.
// ═══════════════════════════════════════════════════════════════════════════

/** The draft, in WIRE shape. `title` is nullable end-to-end (the column is), and
 *  a blank title settles back to `null` on blur so it cannot re-send forever —
 *  see QuestionDetailForm's `blank_accepted_variants` for the same pattern. That
 *  is a wire-level nullable and NOT the `''`-vs-absent rule: `body` cleared to
 *  `''` is a real value and IS sent. */
type ReadingDraft = {
  title: string | null;
  body: string;
  sort_order: number;
  image_s3_keys: string[];
};

/** One source for each field's on-screen label — the `<label>` the editor reads
 *  and the name the error banner uses are the same string by construction. */
export const READING_FIELD_LABELS: Record<keyof ReadingDraft, string> = {
  title: 'Title',
  body: 'Body',
  sort_order: 'Position',
  image_s3_keys: 'Images',
};

/** Copy that is not a field label. Constants, not literals — a literal-text grep
 *  must not return a true zero for what is on screen. */
export const READING_COPY = {
  /** feat-259: this said "…automatically ON SAVE" in a panel that no longer has
   *  a Save (design/NOTES.md §3). */
  autoTranslated: '🌐 Spanish translated automatically',
  bodyRequired: 'Body is required',
  uploadInFlight: 'An image is still uploading.',
} as const;

export interface ReadingDetailFormProps extends PanelSaveWiring {
  tenantId: string;
  lessonId: string;
  /** null = create a new reading item; otherwise edit this id. */
  existingId: string | null;
  /** The selected row (edit mode) — seeds title/body/images without a round-trip. */
  summary?: ReadingItemSummary | null;
  /** Default sort position for a NEW item. */
  defaultSort: number;
  /** Fired ONCE, when the first blur creates the row. A create changes which
   *  rows exist, so unlike an auto-save it still reloads (AC10). */
  onCreated: (savedId: string) => void;
  /** Present in edit mode — soft-deletes the item. */
  onDelete?: () => void;
  /** The shell's item-type switcher for a NEW item. Rendered at the top of the
   *  SCROLL CHILD: this component owns the `<aside>` now, so the control cannot
   *  stay a sibling of it in the shell without scrolling the footer away. */
  typeControl?: React.ReactNode;
}

export const ReadingDetailForm: React.FC<ReadingDetailFormProps> = ({
  tenantId,
  lessonId,
  existingId,
  summary,
  defaultSort,
  onCreated,
  onDelete,
  typeControl,
  registerFlush,
  pendingNavigation = false,
  onNavigationProceed,
  onPatched,
}) => {
  const [uploading, setUploading] = useState(false);
  const uploadingRef = useRef(false);
  const [imageError, setImageError] = useState<string | null>(null);

  // Thumbnails live alongside the draft: the WIRE field is the key list, but the
  // UI needs a URL per key and the server does not echo one back for a key we
  // just uploaded.
  const [imageUrls, setImageUrls] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    (summary?.image_s3_keys ?? []).forEach((key, i) => {
      seed[key] = summary?.image_urls?.[i] ?? lessonImagePublicUrl(key) ?? '';
    });
    return seed;
  });

  const save = useAutoSave<ReadingDraft>({
    initial: {
      title: summary?.title ?? null,
      body: summary?.body ?? '',
      sort_order: summary?.sort_order ?? defaultSort,
      image_s3_keys: summary?.image_s3_keys ?? [],
    },
    itemId: existingId,
    labels: READING_FIELD_LABELS,
    create: async (draft) => {
      const { id } = await createReadingItem(lessonId, {
        tenant_id: tenantId,
        title: draft.title,
        body: draft.body,
        sort_order: draft.sort_order,
        image_s3_keys: draft.image_s3_keys,
      });
      return id;
    },
    update: (id, changed) => updateReadingItem(id, changed),
    // brief §6.4 — the "body is required" half of the old `canSave` becomes an
    // advisory, but the `uploading` half stays REAL. Nothing is dropped: the
    // fields stay dirty and the effect below flushes when the upload lands.
    holdWrites: () => (uploadingRef.current ? READING_COPY.uploadInFlight : null),
    onPersisted: ({ id, changed, created }) => {
      if (created) onCreated(id);
      else onPatched?.(id, changed as Record<string, unknown>);
    },
  });

  const isEdit = existingId !== null;
  const { draft } = save;
  const title = draft.title ?? '';
  const body = draft.body;
  const sortOrder = draft.sort_order;
  const images: StagedImage[] = draft.image_s3_keys.map((s3_key) => ({
    s3_key,
    url: imageUrls[s3_key] ?? lessonImagePublicUrl(s3_key) ?? '',
  }));

  useEffect(() => {
    registerFlush?.(save.flush);
    return () => registerFlush?.(null);
  }, [registerFlush, save.flush]);

  const setImages = useCallback(
    (next: StagedImage[] | ((prev: StagedImage[]) => StagedImage[])) => {
      const resolved =
        typeof next === 'function'
          ? (next as (p: StagedImage[]) => StagedImage[])(
              save.draft.image_s3_keys.map((k) => ({ s3_key: k, url: imageUrls[k] ?? '' })),
            )
          : next;
      setImageUrls((prev) => {
        const merged = { ...prev };
        resolved.forEach((im) => { merged[im.s3_key] = im.url || merged[im.s3_key] || ''; });
        return merged;
      });
      // AC2 — add / remove / reorder is a non-text control: it writes on change.
      save.saveField('image_s3_keys', resolved.map((im) => im.s3_key));
    },
    [save, imageUrls],
  );
  // Body editor is a two-state Edit ⇄ Preview toggle (mockup §C), not a
  // side-by-side split — keeps the narrow right pane readable.
  const [editorMode, setEditorMode] = useState<ReadingEditorMode>('edit');
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const bodyTrimmed = body.trim();
  const overSoftCap = body.length > READING_BODY_SOFT_WARN_CHARS;

  // `holdWrites` runs inside an async callback, so it must read a REF — a state
  // value would be the one captured at the render the callback was made in.
  useEffect(() => {
    uploadingRef.current = uploading;
    // The hold has lifted: write whatever piled up behind it.
    if (!uploading && save.isDirty) void save.flush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploading]);

  const acceptFiles = async (files: FileList) => {
    setImageError(null);
    const list = Array.from(files);
    uploadingRef.current = true;
    setUploading(true);
    try {
      for (const file of list) {
        if (!LESSON_IMAGE_ALLOWED_TYPES.includes(file.type)) {
          setImageError(`${file.name || 'That file'} isn't a supported image. Use PNG, JPEG, or WebP.`);
          continue;
        }
        if (file.size > LESSON_IMAGE_MAX_BYTES) {
          setImageError(
            `${humanBytes(file.size)} exceeds the ${humanBytes(LESSON_IMAGE_MAX_BYTES)} image limit.`,
          );
          continue;
        }
        const { s3_key } = await uploadReadingImage(file, tenantId, lessonId);
        setImages((prev) => [...prev, { s3_key, url: lessonImagePublicUrl(s3_key) ?? '' }]);
      }
    } catch (e) {
      setImageError(String(e));
    } finally {
      setUploading(false);
    }
  };

  const moveImage = (index: number, dir: -1 | 1) => {
    setImages((prev) => {
      const to = index + dir;
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[to]] = [next[to], next[index]];
      return next;
    });
  };
  const removeImage = (index: number) =>
    setImages((prev) => prev.filter((_, i) => i !== index));

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length) acceptFiles(e.dataTransfer.files);
  };

  /** Light markdown toolbar: wrap the current selection (or insert a placeholder)
   *  with the given markers, then restore focus. Line-prefix formats (heading,
   *  bullet) prepend at the line start instead of wrapping. */
  const applyFormat = (kind: 'bold' | 'italic' | 'heading' | 'list' | 'link') => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = body.slice(start, end);
    let next = body;
    let caret = end;
    if (kind === 'bold' || kind === 'italic') {
      const mark = kind === 'bold' ? '**' : '*';
      const inner = selected || (kind === 'bold' ? 'bold text' : 'italic text');
      next = body.slice(0, start) + mark + inner + mark + body.slice(end);
      caret = start + mark.length + inner.length + mark.length;
    } else if (kind === 'link') {
      const label = selected || 'link text';
      next = body.slice(0, start) + `[${label}](https://)` + body.slice(end);
      caret = start + `[${label}](https://)`.length;
    } else {
      // Line-prefix formats: find the start of the current line.
      const lineStart = body.lastIndexOf('\n', start - 1) + 1;
      const prefix = kind === 'heading' ? '## ' : '- ';
      next = body.slice(0, lineStart) + prefix + body.slice(lineStart);
      caret = end + prefix.length;
    }
    save.setField('body', next);
    setEditorMode('edit');
    // Restore focus + caret after React re-renders the textarea.
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const handleDelete = () => onDelete?.();

  const handleNavRetry = async () => {
    if (await save.flush()) onNavigationProceed?.();
  };

  /** The failing field gains a hairline so it is findable when the form is
   *  scrolled past it (design/error-states.html state A). */
  const fieldBorder = (field: keyof ReadingDraft) =>
    save.error?.fields.includes(field)
      ? (save.error.kind === 'validation' ? 'border-attention-line' : 'border-danger-line')
      : 'border-softBorder';

  const charCount = useMemo(() => body.length, [body]);

  return (
    // feat-259 / design/NOTES.md §3 — the RESTRUCTURE. This used to be a plain
    // `<div>` inside an `overflow-y-auto` aside, which has no scroll-area /
    // footer split at all: a footer added inside it would scroll away with the
    // fields, reproducing feat-231's bug while passing a DOM-shape comparison.
    // Padding moves OFF the aside and onto the scroll child — left on the aside
    // it puts 16px of dead ground under the footer.
    <aside className="border-l border-softBorder bg-cream/40 flex flex-col min-h-0 overflow-hidden">
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
        {/* feat-270 panels 4 / 9 — ONE panel-level block, never one per field. */}
        <PanelApprovalState />
        {typeControl}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold text-reading uppercase tracking-wide">📖 Reading</div>
          <p className="text-[11px] text-inkMuted mt-0.5">
            Explanatory text the learner reads, then taps Continue. No XP.
          </p>
          {/* feat-259 — `stacked` STAYS, and design/NOTES.md §3 asks for `inline`.
              I did not apply it, and this is why.
                `inline` DOES NOT RENDER `updated` AT ALL. It is not a layout
              variant with the same content: `ContentTimestamps.tsx` computes
              `updatedText` as `variant === 'stacked' ? format(updated) : null`,
              and its own doc says so — "`inline` ignores it — those surfaces
              have no `updated`". Lesson and Drill genuinely have none. A reading
              item HAS one, feat-252's AC-F4 asserts this panel renders it, and
              two of those tests go red on the switch. Switching would delete a
              field the editor can see today.
                feat-252's rule is also about content, not just a free edge:
              "Every surface that can show two lines is `stacked`, so the
              two-line case never has to fit on a fixed-height row." Reading can
              show two lines, so it is `stacked` by that rule regardless of what
              Delete does.
                Raised with `design`. If they want the line of vertical space
              back it needs `inline` to learn `updated`, which is a change to a
              feat-252 component and its ADR — not something to do in flight
              here. Delete still moves to the footer; that half is applied. */}
          <ContentTimestamps
            variant="stacked"
            className="mt-0.5"
            created={summary?.created}
            updated={summary?.updated}
          />
        </div>
      </div>

      {/* title (optional) */}
      <label className="block">
        <span className="text-xs text-inkMuted">
          Title (optional)
          <ApprovedFieldMarker field="title" />
        </span>
        <input
          className={`mt-1 w-full rounded-lg border ${fieldBorder('title')} px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reading/40`}
          value={title}
          onChange={(e) => save.setField('title', e.target.value)}
          onBlur={() => {
            // Settle a blank title back to `null` BEFORE committing, so the
            // field can reach a clean state instead of re-sending `'' → null`
            // on every later blur.
            const trimmed = title.trim();
            const settled = trimmed === '' ? null : trimmed;
            if (settled !== draft.title) save.setField('title', settled);
            save.commitField('title');
          }}
          placeholder="e.g. Before you start"
        />
        {/* §10.4 — inside the label, which is this form's field container, so it
            shares a parent with the input rather than becoming a `space-y-4`
            sibling. */}
        <ApprovedValueLine field="title" />
      </label>

      {/* body: markdown source with an Edit ⇄ Preview toggle (mockup §C) */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-inkMuted">Body (Markdown)</span>
          <EditPreviewToggle mode={editorMode} onChange={setEditorMode} />
        </div>

        {editorMode === 'edit' ? (
          <div className="rounded-lg border border-softBorder overflow-hidden focus-within:ring-2 focus-within:ring-reading/40">
            {/* light markdown toolbar */}
            <div className="flex items-center gap-0.5 border-b border-softBorder bg-cream/50 px-1.5 py-1">
              <button type="button" onClick={() => applyFormat('bold')} className="px-2 py-0.5 rounded hover:bg-white text-sm font-bold text-ink" aria-label="Bold" title="Bold">B</button>
              <button type="button" onClick={() => applyFormat('italic')} className="px-2 py-0.5 rounded hover:bg-white text-sm italic text-ink" aria-label="Italic" title="Italic">I</button>
              <button type="button" onClick={() => applyFormat('heading')} className="px-2 py-0.5 rounded hover:bg-white text-sm font-bold text-ink" aria-label="Heading" title="Heading">H</button>
              <button type="button" onClick={() => applyFormat('list')} className="px-2 py-0.5 rounded hover:bg-white text-sm text-ink" aria-label="Bullet list" title="Bullet list">•</button>
              <button type="button" onClick={() => applyFormat('link')} className="px-2 py-0.5 rounded hover:bg-white text-sm text-ink" aria-label="Link" title="Link">🔗</button>
            </div>
            <textarea
              ref={bodyRef}
              className="w-full min-h-[220px] px-3 py-2 text-sm font-mono leading-relaxed focus:outline-none resize-y"
              value={body}
              onChange={(e) => save.setField('body', e.target.value)}
              onBlur={() => save.commitField('body')}
              placeholder={READING_BODY_PLACEHOLDER}
              aria-label="Reading body (Markdown)"
            />
          </div>
        ) : (
          <div className="rounded-lg border border-softBorder bg-white px-3 py-2 overflow-y-auto min-h-[220px] max-h-[400px]">
            <MarkdownPreview source={body} />
            <p className="mt-3 pt-2 border-t border-softBorder/60 text-[10px] text-inkMuted">
              {READING_PREVIEW_PARITY_NOTE}
            </p>
          </div>
        )}
        <ApprovedValueLine field="body" />
        <div className="mt-1 flex items-center justify-between text-[11px]">
          {/* `warning-on` on this ground is 1.999:1 — an AA failure by a factor
              of 2.25 (feat-248). `attention-on` is the same gold darkened until
              it passes (design/NOTES.md §1). */}
          <span className={overSoftCap ? 'text-attention-on font-semibold' : 'text-inkMuted'}>
            {readingCharCountText(charCount)}
          </span>
          {/* spec §Incomplete items save anyway — an ADVISORY now. It gates
              nothing; the item persists empty and the publish guard is what
              stops it reaching a learner. */}
          {bodyTrimmed.length === 0 && <span className="text-inkMuted">{READING_COPY.bodyRequired}</span>}
        </div>
      </div>

      {/* optional ordered images — rendered inline-after-body on mobile (v1) */}
      <div>
        <div className="mb-1.5">
          <span className="text-xs text-inkMuted">
            Images {images.length > 0 ? `· ${images.length}` : ''}{' '}
            <span className="text-inkMuted/70">(optional, shown in order after the text)</span>
          </span>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files.length) acceptFiles(e.target.files);
            e.target.value = '';
          }}
        />

        {images.length > 0 && (
          <ul className="space-y-1.5 mb-2">
            {images.map((im, i) => (
              <li
                key={im.s3_key}
                className="flex items-center gap-2 rounded-xl border border-softBorder bg-white px-2 py-1.5"
              >
                <span className="text-[10px] font-bold text-inkMuted w-4 text-center shrink-0">{i + 1}</span>
                {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                <img src={im.url} alt={`Reading image ${i + 1}`} className="w-12 h-12 rounded object-cover bg-cream shrink-0" />
                <span className="flex-1 min-w-0 truncate text-[11px] text-inkMuted font-mono">{im.s3_key}</span>
                <button
                  type="button"
                  className="text-inkMuted hover:text-ink disabled:opacity-30 px-1"
                  onClick={() => moveImage(i, -1)}
                  disabled={i === 0}
                  aria-label={`Move image ${i + 1} up`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="text-inkMuted hover:text-ink disabled:opacity-30 px-1"
                  onClick={() => moveImage(i, 1)}
                  disabled={i === images.length - 1}
                  aria-label={`Move image ${i + 1} down`}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="text-coral hover:underline text-[11px] font-semibold px-1"
                  onClick={() => removeImage(i)}
                  aria-label={`Remove image ${i + 1}`}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* dropzone / add affordance */}
        <div
          onClick={() => !uploading && fileRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`rounded-xl border-2 border-dashed p-4 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-reading bg-reading-light' : 'border-reading-border bg-reading-light/40 hover:bg-reading-light/70'
          } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
        >
          {uploading ? (
            <div className="text-[11px] text-inkMuted inline-flex items-center gap-2">
              <Spinner size={14} /> Uploading…
            </div>
          ) : (
            <>
              <div className="text-xl">🖼️</div>
              <div className="text-[11px] text-inkMuted mt-1">
                {images.length > 0 ? 'Add another image' : 'Drop images or '}
                {images.length === 0 && <span className="text-reading font-semibold underline">browse</span>}
              </div>
              <div className="text-[10px] text-inkMuted/70 mt-0.5">
                PNG / JPEG / WebP · ≤ {humanBytes(LESSON_IMAGE_MAX_BYTES)} each · rendered after the text
              </div>
            </>
          )}
        </div>
        {imageError && <div className="mt-1.5"><ErrorBanner title="Can't use that image">{imageError}</ErrorBanner></div>}
      </div>

      {/* position */}
      <label className="block max-w-[160px]">
        <span className="text-xs text-inkMuted">Position (sort)</span>
        <input
          type="number"
          className="mt-1 w-full rounded-lg border border-softBorder px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reading/40"
          value={sortOrder}
          onChange={(e) => save.setField('sort_order', parseInt(e.target.value, 10) || 0)}
          onBlur={() => save.commitField('sort_order')}
        />
      </label>

      {/* feat-018: reading title/body auto-translate EN→ES (no side-by-side ES
          review in v1 — unlike video captions). It belongs WITH THE FIELDS: the
          footer's two slots are Delete and the save status, nothing else. */}
      <span className="text-[11px] text-inkMuted inline-flex items-center gap-1">
        {READING_COPY.autoTranslated}
      </span>
      </div>

      {/* Banner and footer are `shrink-0` SIBLINGS of the scroll area above, so
          growth eats the field list and never the footer. */}
      <SaveErrorBanner
        error={save.error as never}
        blockedReason={save.blockedReason}
        pendingNavigation={pendingNavigation}
        typeSingular={QUIZ_TERMS.reading.singular}
        onRetry={pendingNavigation ? handleNavRetry : save.retry}
        onLeaveAnyway={onNavigationProceed}
      />
      <ItemDetailFooter
        onDelete={isEdit && onDelete ? handleDelete : undefined}
        status={save.status}
        savedAt={save.savedAt}
      />
    </aside>
  );
};

export default ReadingDetailForm;
